import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Copy, Pause, Play, RotateCcw, UploadCloud, X } from 'lucide-react';
import { uploader, useUploads, type UploadItem } from '../upload/uploader';
import { bytes, rate } from '../lib/format';
import { PlaceholderPicker } from './PlaceholderPicker';

function describeMasterDone(i: UploadItem, m: NonNullable<UploadItem['master']>): string {
  const o = i.masterOutcome;
  if (o?.kind === 'pending') return `Uploaded · still being checked; it appears on ${m.assetCode}’s Masters tab once verified`;
  if (o?.kind === 'kept') return `${m.assetCode} already has this ${m.variantName} master; the existing one is kept`;
  return o?.label ? `Added as ${o.label} on ${m.assetCode}` : `Added as a ${m.variantName} master of ${m.assetCode}`;
}

function describe(i: UploadItem): string {
  switch (i.state) {
    case 'queued': return 'Waiting…';
    case 'uploading': return `${bytes(i.sent)} of ${bytes(i.total)}${i.rate ? ` · ${rate(i.rate)}` : ''}`;
    case 'paused': return `Paused at ${bytes(i.sent)} of ${bytes(i.total)}`;
    case 'retrying': return i.error ?? 'Retrying…';
    case 'finalizing': return 'Verifying checksum and registering…';
    case 'done': return i.master ? describeMasterDone(i, i.master)
      : i.version ? `Uploaded as version ${i.versionOutcome?.number ?? i.version.expectedVersion + 1} of ${i.version.assetCode} · processing started${i.versionOutcome?.warning ? `. ${i.versionOutcome.warning}` : ''}`
      : 'Uploaded · processing started';
    case 'duplicate': return `Identical content already exists as “${i.duplicate?.existingTitle}”`;
    case 'master_exists': return i.error ?? `${i.master?.assetCode ?? 'The asset'} already has a ${i.master?.variantName ?? ''} master of this format`;
    case 'error': return i.error ?? 'Upload failed';
    case 'cancelled': return 'Cancelled';
  }
}

export function UploadTray() {
  const items = useUploads();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const visible = items.filter((i) => i.state !== 'cancelled');
  if (!visible.length) return null;
  const activeCount = visible.filter((i) => ['queued', 'uploading', 'retrying', 'finalizing'].includes(i.state)).length;
  const allDone = visible.every((i) => i.state === 'done');

  return (
    <section className="upload-tray" aria-label="Uploads" data-testid="upload-tray">
      <header>
        {allDone ? <CheckCircle2 size={16} color="var(--ready)" /> : <UploadCloud size={16} color="var(--accent-strong)" />}
        <span style={{ flex: 1 }}>{activeCount ? `Uploading ${activeCount} file${activeCount > 1 ? 's' : ''}` : 'Uploads'}</span>
        {!activeCount && <button className="btn ghost sm" onClick={() => uploader.clearFinished()}>Clear</button>}
        <button className="btn ghost icon sm" aria-label={collapsed ? 'Expand uploads' : 'Collapse uploads'} onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </header>
      {!collapsed && (
        <div className="upload-list">
          {visible.map((i) => (
            <div className="upload-item" key={i.localId} data-state={i.state}>
              <div className="row">
                <span className="name" title={i.file.name}>{i.file.name}</span>
                {i.state === 'uploading' && <button className="btn ghost icon sm" aria-label="Pause" onClick={() => uploader.pause(i.localId)}><Pause size={14} /></button>}
                {(i.state === 'paused' || i.state === 'error') && i.uploadId !== undefined && (
                  <button className="btn ghost icon sm" aria-label="Resume" onClick={() => uploader.resume(i.localId)}>{i.state === 'error' ? <RotateCcw size={14} /> : <Play size={14} />}</button>
                )}
                {i.state === 'done' && i.assetId && <button className="btn ghost sm" onClick={() => navigate(`/assets/${i.assetId}`)}>Open</button>}
                {!['done', 'finalizing'].includes(i.state) && (
                  <button className="btn ghost icon sm" aria-label="Cancel upload" onClick={() => void uploader.cancel(i.localId)}><X size={14} /></button>
                )}
              </div>
              {['uploading', 'paused', 'retrying', 'queued', 'finalizing'].includes(i.state) && (
                <div className={`progress ${i.state === 'finalizing' ? 'indeterminate' : ''}`}>
                  <span style={{ width: `${(i.sent / Math.max(1, i.total)) * 100}%` }} />
                </div>
              )}
              {i.master && i.state !== 'done' && (
                <div className="sub" data-testid="upload-master-target"><span>Master of {i.master.assetCode} · {i.master.variantName}</span></div>
              )}
              {i.version && i.state !== 'done' && (
                <div className="sub" data-testid="upload-version-target"><span>New version of {i.version.assetCode} (version {i.version.expectedVersion + 1})</span></div>
              )}
              {!i.master && !i.version && ['queued', 'uploading', 'paused', 'retrying', 'error'].includes(i.state) && (
                <div className="row" data-testid="upload-target">
                  <PlaceholderPicker value={i.placeholder} onChange={(p) => uploader.setPlaceholder(i.localId, p)} />
                </div>
              )}
              {i.state === 'done' && i.placeholder && <div className="sub"><span>Filled {i.placeholder.assetCode} · {i.placeholder.title}</span></div>}
              <div className="sub" style={{ color: i.state === 'error' ? 'var(--danger)' : i.state === 'duplicate' || i.state === 'master_exists' ? 'var(--warning)' : undefined }} data-testid="upload-status">
                {(i.state === 'error' || i.state === 'master_exists') && <AlertTriangle size={13} />}
                {i.state === 'duplicate' && <Copy size={13} />}
                <span>{describe(i)}</span>
              </div>
              {i.state === 'master_exists' && (
                <div className="row" style={{ gap: 6 }}>
                  <button className="btn sm" onClick={() => uploader.replaceMaster(i.localId)} data-testid="replace-master"
                    title="The existing master is kept until this one is verified, then removed">Replace it</button>
                  <button className="btn ghost sm" onClick={() => void uploader.cancel(i.localId)}>Keep the existing one</button>
                </div>
              )}
              {i.state === 'duplicate' && i.duplicate && (
                <div className="row" style={{ gap: 6 }}>
                  <button className="btn sm" onClick={() => navigate(`/assets/${i.duplicate!.existingAssetId}`)}>Open existing</button>
                  <button className="btn sm" onClick={() => uploader.importAnyway(i.localId)}>{i.placeholder ? 'Import anyway' : i.version ? 'Upload the version anyway' : 'Import as new asset'}</button>
                  <button className="btn ghost sm" onClick={() => void uploader.cancel(i.localId)}>Discard</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
